import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '@/utils/httpClient';
import { useAuth } from '@/hooks/useAuth';
import type { ThemeMode } from '@/stores/useThemePrefsStore';
import type { AccessibilityPrefs } from '@/stores/useAccessibilityPrefsStore';

export interface UserPreferences {
  menu_favorites?: string[];
  theme_mode?: ThemeMode;
  accessibility?: AccessibilityPrefs;
}

const PREFS_QUERY_KEY = ['user-preferences'];

export const useUserPreferences = () => {
  const { isAuthenticated } = useAuth();
  return useQuery<UserPreferences>({
    queryKey: PREFS_QUERY_KEY,
    queryFn: async () => {
      const result = await httpClient.post<UserPreferences>('/api/v1/rpc/get_my_preferences', {});
      return result ?? {};
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: isAuthenticated,
  });
};

export const useSaveUserPreferences = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (preferences: Partial<UserPreferences>) => {
      await httpClient.post('/api/v1/rpc/save_my_preferences', {
        p_preferences: preferences,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PREFS_QUERY_KEY });
    },
  });
};
